import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";

type FriendSummary = { id: string; username?: string; name?: string; avatarUrl?: string | null; status?: string | null; nameColor?: string | null };

function loadFriendsCache() {
  try {
    const raw = localStorage.getItem('__friends_cache__');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// Per-account record of which incoming friend-request ids the user has already
// seen (by opening the Friends modal). The badge counts only *unseen* requests,
// so the 45s/focus poll can't resurrect the badge for requests already viewed.
function loadSeenReqIds(uid: string): Set<string> {
  try {
    const raw = localStorage.getItem(`friendReq:seenIds:${uid}`);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}
function saveSeenReqIds(uid: string, ids: Set<string>) {
  try { localStorage.setItem(`friendReq:seenIds:${uid}`, JSON.stringify([...ids])); } catch { /* noop */ }
}

type UseFriendsOptions = {
  globalUserIds: string[];
  // Current account id — keys the per-user seen-request record.
  userId?: string;
  // Deferred wrapper around useNotifications' setter (see the ChatLayout call
  // site): refreshFriends reports the incoming-request count through it.
  setFriendReqCount: (n: number) => void;
};

/**
 * The friends list (localStorage cache load + persist + server refresh on
 * mount/focus/45s interval), the Friends-modal open flag, and the DM
 * friend-search box state with its derived online/filtered lists (step 4.1).
 *
 * refreshFriends moved here from ChatLayout (it stayed parent-side during 3.3
 * precisely because friendsList belonged to this step); it still feeds the
 * friend-request badge via the setFriendReqCount input. Consumers of the
 * online/filtered lists (DM sidebar, landing widgets, people panel) stay in
 * ChatLayout; presenceFromStatus stayed too — it's a presence-display helper,
 * not friends state.
 */
export function useFriends({ globalUserIds, userId, setFriendReqCount }: UseFriendsOptions) {
  const [friendsList, setFriendsList] = useState<FriendSummary[]>(loadFriendsCache());
  // Read userId through a ref so the mount-time refresh/interval closures always
  // see the current account, not a stale one captured at first render.
  const userIdRef = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  // Load and refresh friends list for DM actions and badges
  async function refreshFriends() {
    try {
      const tok = getAuthToken() || '';
      const r = await api.getAuth('/friends/list', tok);
      const list: FriendSummary[] = Array.isArray(r?.friends) ? r.friends : [];
      setFriendsList(list);
      try {
        const rq = await api.getAuth('/friends/requests', tok);
        const incoming: any[] = Array.isArray(rq?.incoming) ? rq.incoming : [];
        const uid = userIdRef.current || '';
        // Badge only requests the user hasn't already seen (opened the modal on),
        // so the periodic poll can't resurrect a badge they've already cleared.
        const seen = uid ? loadSeenReqIds(uid) : new Set<string>();
        const unseen = incoming.filter((req) => !seen.has(String(req?.id))).length;
        setFriendReqCount(unseen);
      } catch { /* noop */ }
    } catch { /* noop */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load once; refreshFriends only reads refs and forwards to a real state setter
  useEffect(() => { refreshFriends(); }, []);
  useEffect(() => {
    const onFocus = () => { refreshFriends(); };
    const interval = setInterval(() => { refreshFriends(); }, 45000);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- polls on a timer; refreshFriends only reads refs and forwards to a real state setter
  }, []);
  // Friends modal open state
  const [friendsOpen, setFriendsOpen] = useState(false);
  // Opening the modal "sees" all current incoming requests: record their ids and
  // zero the badge. (useNotifications also zeroes the count on open for instant
  // feedback; this effect additionally persists the seen set.)
  useEffect(() => {
    if (!friendsOpen) return;
    (async () => {
      try {
        const tok = getAuthToken() || '';
        const rq = await api.getAuth('/friends/requests', tok);
        const incoming: any[] = Array.isArray(rq?.incoming) ? rq.incoming : [];
        const uid = userIdRef.current || '';
        if (uid) saveSeenReqIds(uid, new Set(incoming.map((req) => String(req?.id))));
      } catch { /* noop */ }
      setFriendReqCount(0);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the modal opens; the setter only forwards to a real state setter
  }, [friendsOpen]);
  const [dmFriendSearch, setDmFriendSearch] = useState('');
  const friendsOnline = useMemo(() => {
    if (!friendsList.length || !globalUserIds.length) return [];
    const onlineSet = new Set(globalUserIds);
    return friendsList.filter((friend) => friend.id && onlineSet.has(friend.id));
  }, [friendsList, globalUserIds]);
  useEffect(() => {
    try { localStorage.setItem('__friends_cache__', JSON.stringify(friendsList)); } catch { /* noop */ }
  }, [friendsList]);
  const filteredFriendsForDmSearch = useMemo(() => {
    const q = dmFriendSearch.trim().toLowerCase();
    const onlineSet = new Set(globalUserIds);
    const sorted = [...friendsList].sort((a, b) => {
      const aOnline = onlineSet.has(String(a.id));
      const bOnline = onlineSet.has(String(b.id));
      if (aOnline !== bOnline) return aOnline ? -1 : 1;
      const an = (a.name || a.username || '').toLowerCase();
      const bn = (b.name || b.username || '').toLowerCase();
      return an.localeCompare(bn);
    });
    const filtered = q
      ? sorted.filter((f) => {
          const label = `${f.name || ''} ${f.username || ''}`.toLowerCase();
          return label.includes(q);
        })
      : sorted;
    return filtered.slice(0, 12);
  }, [dmFriendSearch, friendsList, globalUserIds]);

  return {
    friendsList,
    refreshFriends,
    friendsOpen,
    setFriendsOpen,
    dmFriendSearch,
    setDmFriendSearch,
    friendsOnline,
    filteredFriendsForDmSearch,
  };
}


